import { Control, FieldValues, useController, useFormContext } from 'react-hook-form';
import { Input } from '@/components/design-system/Input';
import { useEffect, useState } from 'react';
import { Checkbox } from '@/components/design-system/Checkbox';

interface Props {
  control: Control<FieldValues>;
  name: string;
  title?: string;
  subtitle?: string;
  required?: boolean;
  disabled?: boolean;
  canSkip?: boolean;
  skipLabel?: string;
  initialAnswer?: { can_skip_value: boolean };
  inputs: Array<{
    ordinal: number;
    type: string;
    metadata: {
      placeholder: string;
      validation?: {
        min?: number;
        max?: number;
      };
    };
  }>;
}

export const MultipleInputWrapper = ({
  control,
  name,
  required = false,
  disabled = false,
  canSkip = false,
  skipLabel,
  inputs,
  initialAnswer,
}: Props) => {
  const { setValue, getValues } = useFormContext();
  const [fieldErrors, setFieldErrors] = useState<(string | null)[]>(
    Array(inputs.length).fill(null),
  );
  const [requiredError, setRequiredError] = useState<string | null>(null);
  const [isSkipped, setIsSkipped] = useState(
    initialAnswer?.can_skip_value === true,
  );

  const getFieldLabel = (placeholder: string) => {
    // Extract the field name from placeholder text
    const match = placeholder.match(/Enter (.*?) \(/);
    if (match && match[1]) {
      return match[1].toLowerCase();
    }
    // Fallback to the full placeholder without "Enter " if no parentheses found
    return placeholder.replace(/^Enter /i, '').toLowerCase();
  };

  const validateField = (value: string, index: number): string | null => {
    if (!value) return null;

    const input = inputs[index];
    const validation = input.metadata.validation;
    const numValue = Number(value);
    const fieldLabel = getFieldLabel(input.metadata.placeholder);

    if (validation?.min !== undefined && numValue < validation.min) {
      return `${fieldLabel} must be at least ${validation.min}`;
    }
    if (validation?.max !== undefined && numValue > validation.max) {
      return `${fieldLabel} must be at most ${validation.max}`;
    }
    return null;
  };

  const { field } = useController({
    name,
    control,
    defaultValue: initialAnswer || [],
    rules: {
      validate: (value) => {
        // If not required, always valid
        if (!required) return true;

        // If skipped and has skip value, valid
        if (
          typeof value === 'object' &&
          value !== null &&
          'can_skip_value' in value
        ) {
          return true;
        }

        // If not skipped, must have all required fields filled
        if (Array.isArray(value)) {
          // Check if all fields are filled
          const allFilled = value.every(
            (v) => v !== undefined && v !== '' && v !== null,
          );
          if (!allFilled) {
            const errorMessage = canSkip
              ? 'Please fill in all fields or use the skip option'
              : 'Please fill in all fields';
            setRequiredError(errorMessage);
            return errorMessage;
          }

          // Check for validation errors
          const newFieldErrors = Array(inputs.length).fill(null);
          let hasError = false;

          for (let i = 0; i < inputs.length; i++) {
            const error = validateField(value[i], i);
            if (error) {
              newFieldErrors[i] = error;
              hasError = true;
            }
          }

          setFieldErrors(newFieldErrors);
          if (hasError) {
            return newFieldErrors.find((error) => error !== null);
          }

          setRequiredError(null);
          return true;
        }

        // If we get here, validation failed
        return canSkip
          ? 'Please fill in all fields or use the skip option'
          : 'Please fill in all fields';
      },
    },
  });

  useEffect(() => {
    if (initialAnswer) {
      // Always set the form value when skipValue is true
      setValue(name, { can_skip_value: true }, { shouldValidate: true });
    } else {
      // Only reset if the current value is the skip object
      const currentValue = getValues(name);
      if (
        currentValue &&
        typeof currentValue === 'object' &&
        currentValue.can_skip_value === true
      ) {
        setValue(name, null, { shouldValidate: true });
      }
    }
  }, [initialAnswer, name, setValue, getValues]);

  const handleInputChange = (index: number, value: string) => {
    // Allow empty value or numbers only
    if (value !== '' && !/^\d*$/.test(value)) {
      return;
    }

    const newValues = [...(field.value || Array(inputs.length).fill(''))];
    newValues[index] = value;
    field.onChange(newValues);

    // Validate and show error immediately
    const error = validateField(value, index);
    const newFieldErrors = [...fieldErrors];
    newFieldErrors[index] = error;
    setFieldErrors(newFieldErrors);

    // Clear required error if all fields are filled
    if (requiredError && newValues.every((v) => v !== undefined && v !== '')) {
      setRequiredError(null);
    }
  };

  const handleSkipChange = (checked: boolean) => {
    setIsSkipped(checked);
    if (checked) {
      // Important: Set the skip value in the form data
      field.onChange({ can_skip_value: true });
      setFieldErrors(Array(inputs.length).fill(null));
      setRequiredError(null);
    } else {
      // Reset to empty array when unskipped
      field.onChange(Array(inputs.length).fill(''));
      // Set required error since no values are entered
      if (required) {
        setRequiredError('All fields must be filled');
      }
    }
  };

  return (
    <div className="flex flex-col min-h-[400px]">
      <div className="flex-grow">
        {!isSkipped && requiredError && (
          <p className="text-sm text-red-500 mb-2">{requiredError}</p>
        )}
        <div className="space-y-4">
          {inputs.map((input, index) => (
            <div key={input.ordinal} className="space-y-2">
              <Input
                type="number"
                placeholder={input.metadata.placeholder}
                value={!isSkipped ? field.value?.[index] || '' : ''}
                onChange={(e) => handleInputChange(index, e.target.value)}
                min={input.metadata.validation?.min}
                max={input.metadata.validation?.max}
                disabled={disabled || isSkipped}
                className={
                  !isSkipped &&
                  (fieldErrors[index] ||
                    (requiredError && !field.value?.[index]))
                    ? 'border-red-500'
                    : ''
                }
              />
              {!isSkipped && fieldErrors[index] && (
                <p className="text-sm text-red-500">{fieldErrors[index]}</p>
              )}
            </div>
          ))}
        </div>
      </div>
      {canSkip && (
        <div className="flex justify-center items-center mt-auto">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${name}-skip`}
              checked={isSkipped}
              onCheckedChange={handleSkipChange}
              disabled={disabled}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary border-2 rounded-[4px]"
            />
            <label
              htmlFor={`${name}-skip`}
              className="text-[15px] text-gray-700 cursor-pointer select-none"
            >
              {skipLabel}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultipleInputWrapper;
